import { zodResolver } from "@hookform/resolvers/zod";
import { fromDate } from "@internationalized/date";
import { MinusIcon, PlusIcon } from "lucide-react";
import {
  Controller,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import type { z } from "zod";

import { subjectIdTypeMap } from "@blis/shared/models/subject";
import * as schemas from "@blis/shared/schemas/subject";

import { Button } from "@/components/ui/button";
import { DatePicker, DatePickerInput } from "@/components/ui/date-picker";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import type { TSubjectDetail } from "@/lib/types";

type UpdateInput = z.input<typeof schemas.update>;
type UpdateOutput = z.output<typeof schemas.update>;
type CreateInput = z.input<typeof schemas.create>;
type CreateOutput = z.output<typeof schemas.create>;

type FormInput = UpdateInput | CreateInput;
type FormOutput = UpdateOutput | CreateOutput;

export type SubjectEdit = Pick<
  TSubjectDetail,
  "id" | "caseId" | "dob" | "sex" | "ids"
>;

export function SubjectForm({
  formId,
  subject,
  onSave,
  startingValue,
}: {
  formId: string;
  subject?: SubjectEdit;
  startingValue?: { value: string; type: "caseId" | "unosId" } | null;
  onSave: (values: FormOutput) => void;
}) {
  const { handleSubmit, control, register } = useForm<
    FormInput,
    unknown,
    FormOutput
  >({
    defaultValues: subject
      ? {
          id: subject.id,
          caseId: subject.caseId,
          dob: subject.dob,
          sex: subject.sex,
          ids: subject.ids
            .filter((id) => id.type !== "caseId") // TODO: force case ID for now
            .map((id) => ({
              ...id,
              type: id.type as "unosId", // type safety?
            })),
          type: "donor",
        }
      : {
          caseId: startingValue?.type === "caseId" ? startingValue.value : "",
          ids:
            startingValue?.type === "unosId"
              ? [{ type: startingValue.type, value: startingValue.value }]
              : [],
          type: "donor",
        },
    resolver: zodResolver(subject ? schemas.update : schemas.create),
  });

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: "ids",
  });

  const onSubmit: SubmitHandler<FormOutput> = (data) => {
    onSave({
      ...data,
      caseId: data.caseId.trim(),
      ids: data.ids.map((id) => ({
        ...id,
        value: id.value.trim(),
      })),
    });
  };

  return (
    <form
      id={formId}
      onSubmit={(evt) => {
        evt.stopPropagation();
        handleSubmit(onSubmit)(evt);
      }}
      className="flex flex-col gap-3"
    >
      {subject && <input type="hidden" {...register("id")} />}

      {/* <Controller
        control={control}
        name="type"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <Select
            selectedKey={subjectType}
            onSelectionChange={(key) => field.onChange(key)}
            validationBehavior="aria"
            isInvalid={!!error}
            isDisabled
          >
            <Label>Type</Label>
            <SelectTrigger />
            <SelectList>
              <SelectItem id="donor">Donor</SelectItem>
              <SelectItem id="product">Product</SelectItem>
            </SelectList>
            <FieldError>{error?.message}</FieldError>
          </Select>
        )}
      /> */}

      <Controller
        control={control}
        name="caseId"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField {...field} validationBehavior="aria" isInvalid={!!error}>
            <Label>Donor ID</Label>
            <Input />
            <FieldError>{error?.message}</FieldError>
          </TextField>
        )}
      />

      {fields.map((arrField, index) => (
        <Controller
          control={control}
          name={`ids.${index}.value`}
          key={arrField.id}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField {...field} validationBehavior="aria" isInvalid={!!error}>
              <Label>
                <div className="flex items-center gap-2">
                  {subjectIdTypeMap[arrField.type] ?? "Other"}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onPress={() => {
                      remove(index);
                    }}
                  >
                    <MinusIcon />
                  </Button>
                </div>
              </Label>
              <Input ref={ref} />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      ))}
      <div className="flex items-center gap-2">
        {fields.every((f) => f.type !== "unosId") && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onPress={() => {
              insert(0, { type: "unosId", value: "" });
            }}
          >
            <PlusIcon />
            UNOS ID
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onPress={() => {
            append({ type: "other", value: "" });
          }}
        >
          <PlusIcon />
          Alternate ID
        </Button>
      </div>
      <Controller
        control={control}
        name="dob"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <div className="mt-2">
            <DatePicker
              aria-label="Date of birth"
              granularity="day"
              hideTimeZone
              validationBehavior="aria"
              isInvalid={!!error}
              {...field}
              value={field.value ? fromDate(field.value, "UTC") : null}
              onChange={(val) => {
                if (val) {
                  field.onChange(val.toDate());
                } else {
                  field.onChange(null);
                }
              }}
            >
              <Label>Date of birth</Label>
              <DatePickerInput className="w-40" />
              <FieldError>{error?.message}</FieldError>
            </DatePicker>
          </div>
        )}
      />

      <Controller
        control={control}
        name="sex"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <RadioGroup
            id="sex-radio"
            name="sex"
            orientation="horizontal"
            value={field.value || ""}
            onChange={(val) => field.onChange(val || null)}
          >
            <Label>Sex</Label>
            <Radio value="" id="sex-unknown">
              Unknown
            </Radio>
            <Radio value="M" id="sex-m">
              Male
            </Radio>
            <Radio value="F" id="sex-f">
              Female
            </Radio>
            <FieldError>{error?.message}</FieldError>
          </RadioGroup>
        )}
      />
    </form>
  );
}
